import React, { useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import api from '../../api/api'
import { accessTokens } from 'api/accessTokenMgmt'
import useStore from 'state/knovStore'
import toast from 'utils/toast'
import TeamLogoCropper from 'components/teams/TeamLogoCropper'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { Camera } from 'lucide-react'
import styles from './create-stream-modal.module.scss'
import { uniqBy, defer } from 'lodash'
import { useNavigate } from 'react-router-dom'

// Host-revenue design handoff: one single-page form, no stepper, no payout UI.
// Hierarchy is required-first — the stream name is the hero field, the logo
// hangs off it, and the remaining choices drop into one hairline settings list
// sharing a single label pattern, so the card reads hero / list / action rather
// than six peers. Revenue share needs no payout input — a host's share lands in
// their existing default wallet. The rate is stated once, inside the Host
// rewards row that owns it, instead of a separate header chip; that row is the
// per-stream switch, since a host may run several streams and leave some out.
export default function CreateStreamModal({ closeModal, panelId, filter }) {
    const navigate = useNavigate()
    const [streamName, setStreamName] = useState('')
    // Public by default: a stream is a destination, and reach is what makes one
    // worth running.
    const [isPublic, setIsPublic] = useState(true)
    const [isCreating, setIsCreating] = useState(false)
    const [error, setError] = useState('')
    const [showLogoCropper, setShowLogoCropper] = useState(false)
    const [selectedLogoImage, setSelectedLogoImage] = useState(null)
    const [logoBlob, setLogoBlob] = useState(null)
    // Host mode only: a host may run several revenue streams, so each new one
    // asks rather than assuming. Defaults on — that is the point of the invite.
    const [hostRevenueEnabled, setHostRevenueEnabled] = useState(true)

    // Host context resolves whether the caller may host (any channel owner unless
    // Treechat revoked them) and at what rate: the Host rewards row and its chip.
    const [hostContext, setHostContext] = useState(null)

    const logoInputRef = useRef(null)

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const data = await api.getMyHostGrant()
                if (!cancelled && data) setHostContext(data)
            } catch (e) {
                // Non-hosts and lookup failures both just get the ordinary form.
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    const activeSpaceId = useStore(state => state.activeSpaceId)
    const userStreams = useStore(state => state.userStreams)
    const set = useStore(state => state.set)

    const handleCreate = async () => {
        if (!streamName.trim()) {
            setError('Please enter a channel name')
            return
        }

        setIsCreating(true)
        setError('')

        try {
            // Create form data to match backend expectations
            const formData = new FormData()
            formData.append('name', streamName.trim())
            formData.append('space_id', activeSpaceId)
            formData.append('admin_id', gon.currentUser.id)
            formData.append('public', String(isPublic))
            // The invite link is the stream's front door for anyone the owner brings
            // from outside; a link that renders "Invite Disabled" is a dead end.
            formData.append('standing_invitations_allowed', 'true')
            if (showHostRewards) {
                formData.append('host_revenue_enabled', String(hostRevenueEnabled))
            }

            // Add logo if one was selected
            if (logoBlob) {
                formData.append('team_image', logoBlob, 'logo.png')
            }

            // Use direct fetch like NewStream component
            const response = await fetch(gon.api.teams, {
                method: 'POST',
                body: formData,
                headers: {
                    ...accessTokens(),
                    Accept: 'application/json',
                },
            })

            const output = await response.json()

            if (output?.team) {
                const newTeam = output.team
                const path = `/stream/${newTeam.id}`

                // Update user space options and store
                await api.updateUserSpaceOptions({
                    last_path: path,
                    filter: { team_ids: [newTeam.id] },
                })
                set({
                    userStreams: uniqBy([...userStreams, newTeam], s => s.id),
                    stream: newTeam,
                    streamId: newTeam.id,
                })

                // The stream switched host rewards on at creation (see
                // Api::V1::TeamsController#activate_host_revenue) — say so.
                if (output.host_grant) {
                    const share = output.host_grant.share_percent ?? 10
                    toast.success(`Channel live — you earn ${share}% of every upvalue here`, {
                        durationMs: 8000,
                    })
                }

                // Navigate to the new stream
                defer(() => navigate(path))
                closeModal()
            } else {
                throw new Error('Failed to create channel')
            }
        } catch (error) {
            console.error('Error creating stream:', error)
            setError('Failed to create channel. Please try again.')
            setIsCreating(false)
        }
    }

    const handleLogoSelect = e => {
        const file = e.target.files[0]
        if (file && file.size > 5 * 1024 * 1024) {
            // 5MB limit for logos
            alert('File too large. Please select an image under 5MB.')
            e.target.value = ''
            return
        }

        if (file) {
            const reader = new FileReader()
            reader.onload = e => {
                setSelectedLogoImage(e.target.result)
                setShowLogoCropper(true)
            }
            reader.readAsDataURL(file)
        }
        e.target.value = ''
    }

    const handleLogoSave = croppedBlob => {
        setLogoBlob(croppedBlob)
        setShowLogoCropper(false)
    }

    const hostShare = hostContext?.grant?.share_percent ?? hostContext?.default_share_percent ?? 10
    // The rate chip and stream switch show for anyone who may host
    // (HostGrant.can_host?). Their color follows the user's currency setting:
    // USD green, sats orange.
    const showHostRewards = Boolean(hostContext?.can_host)
    const { useUsd } = useBsvDisplay({ enabled: false })

    return (
        <div className={styles.modalContent}>
            <div className={styles.grabHandle} aria-hidden="true" />

            <h2 className={styles.title}>Create your channel</h2>

            <div className={styles.nameBlock}>
                <label className={styles.fieldLabel} htmlFor="create-stream-name">
                    Channel name
                </label>
                <div className={styles.nameInputWrap}>
                    <input
                        id="create-stream-name"
                        type="text"
                        className={cn(styles.nameInput, error && styles.inputError)}
                        placeholder="Name your channel..."
                        value={streamName}
                        onChange={e => {
                            setStreamName(e.target.value)
                            setError('')
                        }}
                        autoFocus
                        maxLength={50}
                    />
                    <div className={styles.charCount}>{streamName.length}/50</div>
                </div>
                {error && <div className={styles.error}>{error}</div>}
            </div>

            <div className={styles.logoRow}>
                <button
                    type="button"
                    className={styles.logoCircle}
                    onClick={() => logoInputRef.current.click()}
                    aria-label="Add a logo"
                >
                    {logoBlob ? (
                        <img
                            className={styles.logoPreviewImage}
                            src={URL.createObjectURL(logoBlob)}
                            alt="Logo preview"
                        />
                    ) : (
                        <Camera size={16} strokeWidth={1.8} aria-hidden="true" />
                    )}
                </button>
                <div className={styles.logoText}>
                    <div className={styles.logoTitle}>
                        {logoBlob ? 'Change logo' : 'Add a logo'}
                    </div>
                    <div className={styles.logoHint}>
                        Optional now, or set later from channel settings.
                    </div>
                </div>
                <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoSelect}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Every remaining choice is one row of this list: label and hint on
                the left, control on the right, hairline between. The last row
                drops its rule, so non-hosts (no Host rewards row) still end on
                the visibility row cleanly. */}
            <div className={styles.settingsList}>
                <div className={styles.settingRow}>
                    <div className={styles.settingRowInner}>
                        <div className={styles.settingLabel}>
                            <span className={styles.settingTitle}>Visibility</span>
                            <span className={styles.settingWarning}>
                                Channel permissions can&apos;t be changed later.
                            </span>
                        </div>
                        <div
                            className={styles.segmented}
                            role="radiogroup"
                            aria-label="Channel visibility"
                            data-testid="create-stream-visibility"
                        >
                            <button
                                type="button"
                                role="radio"
                                aria-checked={isPublic}
                                className={cn(styles.segment, isPublic && styles.segmentSelected)}
                                onClick={() => setIsPublic(true)}
                                disabled={isCreating}
                            >
                                Public
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={!isPublic}
                                className={cn(styles.segment, !isPublic && styles.segmentSelected)}
                                onClick={() => setIsPublic(false)}
                                disabled={isCreating}
                            >
                                Private
                            </button>
                        </div>
                    </div>
                </div>

                {showHostRewards && (
                    <div
                        className={cn(
                            styles.settingRow,
                            useUsd ? styles.hostRewardsUsd : styles.hostRewardsSats,
                        )}
                    >
                        <div className={styles.settingRowInner}>
                            <div className={styles.settingLabel}>
                                <span className={styles.settingTitle}>Host rewards</span>
                                <span className={styles.settingHint}>
                                    {hostRevenueEnabled ? 'You earn ' : 'Enable to earn '}
                                    <strong
                                        className={styles.hostRateValue}
                                        data-testid="create-stream-host-rate"
                                    >
                                        {hostShare}% of member upvalues
                                    </strong>
                                    . Change any time in channel settings.
                                </span>
                            </div>
                            <div
                                className={styles.segmented}
                                role="radiogroup"
                                aria-label="Host rewards for this channel"
                                data-testid="create-stream-host-rewards"
                            >
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={hostRevenueEnabled}
                                    className={cn(
                                        styles.segment,
                                        hostRevenueEnabled && styles.hostRewardsSegmentOn,
                                    )}
                                    onClick={() => setHostRevenueEnabled(true)}
                                    disabled={isCreating}
                                >
                                    On
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={!hostRevenueEnabled}
                                    className={cn(
                                        styles.segment,
                                        !hostRevenueEnabled && styles.segmentSelected,
                                    )}
                                    onClick={() => setHostRevenueEnabled(false)}
                                    disabled={isCreating}
                                >
                                    Off
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.footer}>
                <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={closeModal}
                    disabled={isCreating}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.createButton}
                    data-testid="create-stream-submit"
                    onClick={handleCreate}
                    disabled={isCreating || !streamName.trim()}
                >
                    {isCreating ? 'Creating...' : 'Create channel'}
                </button>
            </div>

            {showLogoCropper && (
                <TeamLogoCropper
                    image={selectedLogoImage}
                    onSave={handleLogoSave}
                    onCancel={() => setShowLogoCropper(false)}
                />
            )}
        </div>
    )
}
